// MatchPicker — "find its receipt". An inline expansion under an unmatched (or
// just-rejected) charge that fetches the engine's best-first candidate receipts
// and lets Dawn link one by hand. A manual link IS Dawn's confirmation: it
// confirms the match and approves the receipt in one move. Desktop-first, but
// collapses to a single readable column on a phone.

import React, { useEffect, useState } from 'react';
import { apiFetchAuthed } from '../apiHelpers';
import { formatEventDate } from '../utils/date-utils';
import { MatchCandidate, ReconTxn, ScoreBreakdown } from './types';
import EvidenceChips, { CHIP, CHIP_GREEN, CHIP_GRAY } from './EvidenceChips';
import { FOCUS } from './keyStyles';
import { buttonClass } from '../components/ui/Button';

const money = (v: unknown) => (v == null || v === '' ? '—' : `$${Math.abs(Number(v)).toFixed(2)}`);

// Build a ScoreBreakdown from a candidate's flat fields so the same chip
// vocabulary renders here. The card is shown as its own chip (below), so last4
// is left false to avoid a duplicate.
function candidateBreakdown(c: MatchCandidate): ScoreBreakdown {
  const total = Number(c.total);
  const hasTip = c.impliedTip != null && c.impliedTip > 0;
  const impliedTipPct = hasTip && total > 0 ? (c.impliedTip! / total) * 100 : undefined;
  return {
    amountExact: !hasTip,
    impliedTip: c.impliedTip,
    impliedTipPct,
    dateDeltaDays: c.dateDeltaDays,
    last4: false,
    vendorScore: c.vendorScore,
    chargeAmbiguous: false,
    receiptContested: false,
  };
}

export default function MatchPicker({
  txn, onLink, onPeek, onMarkException, busy,
}: {
  txn: ReconTxn;
  onLink: (receiptId: number) => void;     // parent confirms + POSTs + reloads
  onPeek: (publicId: string) => void;      // opens the slide-over without linking
  onMarkException: () => void;             // empty-state shortcut into the exception flow
  busy: boolean;
}) {
  const [candidates, setCandidates] = useState<MatchCandidate[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCandidates(null);
    setError(null);
    apiFetchAuthed(`/reconciliation/transactions/${txn.id}/candidates`, { silent: true })
      .then((res: { candidates: MatchCandidate[] }) => { if (!cancelled) setCandidates(res?.candidates || []); })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Could not load nearby receipts'); });
    return () => { cancelled = true; };
  }, [txn.id]);

  const chargeLast4 = txn.card_last4;

  return (
    <div className="border-l-2 border-primary/50 bg-surface-container-low px-4 py-3">
      <div className="text-xs font-semibold text-on-surface-variant mb-2">Find its receipt</div>

      {error ? (
        <div className="text-sm text-red-200/90 py-1">{error}</div>
      ) : candidates === null ? (
        <div className="space-y-1.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 rounded-md bg-surface-container-high animate-pulse motion-reduce:animate-none" />
          ))}
        </div>
      ) : candidates.length === 0 ? (
        <div className="flex flex-col items-start gap-2 py-1">
          <p className="text-sm text-on-surface-variant">No plausible receipts nearby. The buyer may not have scanned it yet.</p>
          <button onClick={onMarkException} className={buttonClass('quiet', 'sm')}>mark exception</button>
        </div>
      ) : (
        <div className="space-y-1.5">
          {candidates.map((c) => (
            <div key={c.receipt_id}
              className="np-state rounded-md bg-surface-container-high">
              <div className="flex items-start justify-between gap-3 px-3 pt-2">
                <button onClick={() => onLink(c.receipt_id)} disabled={busy}
                  className={`group min-w-0 flex-1 rounded text-left disabled:opacity-50 disabled:cursor-not-allowed ${FOCUS}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-sm text-on-surface group-hover:text-on-surface truncate">{c.vendor_name || 'Receipt'}</span>
                    <span className="text-sm font-mono tabular-nums text-on-surface shrink-0">{money(c.total)}</span>
                  </div>
                  <div className="text-[11px] text-on-surface-variant mt-0.5 truncate">
                    {c.receipt_date ? formatEventDate(c.receipt_date) : '—'}{c.owner_name ? ` · ${c.owner_name}` : ''}
                  </div>
                </button>
                <button onClick={() => onPeek(c.receipt_public_id)}
                  className={`${buttonClass('quiet', 'sm')} shrink-0`}>peek</button>
              </div>
              <div className="flex flex-wrap items-center gap-1 px-3 pb-2 mt-1.5">
                {c.card_last4 && (
                  <span className={`${CHIP} ${c.card_last4 === chargeLast4 ? CHIP_GREEN : CHIP_GRAY}`}>•• {c.card_last4}</span>
                )}
                <EvidenceChips breakdown={candidateBreakdown(c)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
